import { bookingBlockedMeta } from "./errorReporting"
import { trpc, trpcRetry } from "@carrot/bff-client"
import { APPOINTMENTS_QUERY_KEY } from "@internal/constants/telemedicine/appointments"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"

// Derived from the client so the consent identifiers can't drift from the server
// contract — passing an unknown consent fails to compile. `.mutate`'s input is typed
// `Input | void`, so exclude the void before indexing.
type SignConsentInput = Exclude<Parameters<typeof trpc["telemedicine"]["signConsent"]["mutate"]>[0], void>
// What the member did on the consent screen(s): one entry per shown consent with whether they signed it.
export type ShownConsents = SignConsentInput["shown"]
// A single consent + whether the member satisfies it and whether it gates Continue (from getConsentStatus).
export type ConsentRequirement = Awaited<ReturnType<typeof trpc["telemedicine"]["getConsentStatus"]["query"]>>[number]
export type ConsentType = ConsentRequirement["type"]

const CONSENT_STATUS_QUERY_KEY = "telemedicine.getConsentStatus"

// The member's per-consent requirements — the consent-gate decision. Its own query so signConsent
// can invalidate just this.
export function useConsentStatus() {
  return useQuery({
    queryKey: [CONSENT_STATUS_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getConsentStatus.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("consent", "failed to load consent requirements")
  })
}

export function useSignConsent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (shown: ShownConsents) => trpc.telemedicine.signConsent.mutate({ shown }),
    // Invalidate the consent query so a remount of the flow reflects the just-signed state instead of
    // gating on the pre-sign value.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONSENT_STATUS_QUERY_KEY] }),
    meta: bookingBlockedMeta("consent", "failed to save consent")
  })
}

// ---- Booking-flow screening (OPS-9280) ----

// Types derived from the client so they can't drift from the server contract.
type GetScreeningQuestionsInput = Exclude<
  Parameters<typeof trpc["telemedicine"]["getScreeningQuestions"]["query"]>[0],
  void
>
export type ScreeningProgram = GetScreeningQuestionsInput["program"]
export type ScreeningAppointmentType = GetScreeningQuestionsInput["appointmentType"]

type SubmitScreeningInput = Exclude<Parameters<typeof trpc["telemedicine"]["submitScreening"]["mutate"]>[0], void>

const SCREENING_QUESTIONS_QUERY_KEY = "telemedicine.getScreeningQuestions"

export function useScreeningQuestions(
  program: ScreeningProgram,
  appointmentType: ScreeningAppointmentType,
  enabled = true
) {
  return useQuery({
    queryKey: [SCREENING_QUESTIONS_QUERY_KEY, program, appointmentType],
    queryFn: () => trpc.telemedicine.getScreeningQuestions.query({ program, appointmentType }),
    enabled,
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("screening", "failed to load screening questions")
  })
}

export function useSubmitScreening() {
  return useMutation({
    mutationFn: (input: SubmitScreeningInput) => trpc.telemedicine.submitScreening.mutate(input),
    meta: bookingBlockedMeta("screening", "failed to submit screening answers")
  })
}

// ---- Booking-flow entry gate (MEM-760 + MEM-780) ----

export type BookingEligibility = Awaited<ReturnType<typeof trpc["telemedicine"]["beginBooking"]["mutate"]>>

// Fires the one-shot `beginBooking` mutation on flow entry and exposes a query-like { data, isLoading }
// so BookingFlow's mount gate is unchanged. It's a mutation (not a query) because it can persist a
// known-data refer-out (MEM-780); re-fires when the program changes, and a repeat for the same program
// is idempotent server-side (write-on-change).
export function useBeginBooking(program: ScreeningProgram) {
  const mutation = useMutation({
    mutationFn: () => trpc.telemedicine.beginBooking.mutate({ program }),
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("eligibility", "failed to start booking (eligibility check)")
  })
  const { mutate } = mutation
  useEffect(() => {
    mutate()
  }, [mutate, program])
  // Loading until the one-shot mutation settles — covers the pre-fire idle tick and the pending state.
  return {
    data: mutation.data,
    isLoading: !mutation.isSuccess && !mutation.isError,
    isError: mutation.isError,
    retry: () => mutate()
  }
}

// ---- Booking-flow visit preferences (MEM-713) ----

// Types derived from the client so they can't drift from the server contract.
export type BookingOffering = Awaited<ReturnType<typeof trpc["telemedicine"]["getBookingOffering"]["query"]>>
export type Offering = BookingOffering["offerings"][number]

const BOOKING_OFFERING_QUERY_KEY = "telemedicine.getBookingOffering"

export function useBookingOffering(program: ScreeningProgram) {
  return useQuery({
    queryKey: [BOOKING_OFFERING_QUERY_KEY, program],
    queryFn: () => trpc.telemedicine.getBookingOffering.query({ program }),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("offering", "failed to load booking options")
  })
}

// localToday is only a query-key marker; a local-midnight crossing must start a fresh index.
export type AvailabilityResult = Awaited<ReturnType<typeof trpc["telemedicine"]["getAvailabilityForDate"]["query"]>>
export type AvailabilitySlot = AvailabilityResult["slots"][number]

type GetAvailableDatesInput = Exclude<Parameters<typeof trpc["telemedicine"]["getAvailableDates"]["query"]>[0], void>

const AVAILABLE_DATES_QUERY_KEY = "telemedicine.getAvailableDates"
const AVAILABILITY_FOR_DATE_QUERY_KEY = "telemedicine.getAvailabilityForDate"

function availabilityContextKey(input: GetAvailableDatesInput, localToday: string): readonly string[] {
  return [input.program, input.offeringId, input.stateCode, input.language, input.timezone, localToday]
}

export function availableDatesQueryKey(input: GetAvailableDatesInput, localToday: string) {
  return [AVAILABLE_DATES_QUERY_KEY, ...availabilityContextKey(input, localToday)] as const
}

export function useAvailableDates(input: GetAvailableDatesInput, options: { localToday: string; enabled?: boolean }) {
  return useQuery({
    queryKey: availableDatesQueryKey(input, options.localToday),
    queryFn: ({ signal }) => trpc.telemedicine.getAvailableDates.query(input, { signal }),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("availability", "failed to load available dates")
  })
}

// No placeholder data: switching dates must never show the previous date's times.
export function useAvailabilityForDate(
  input: GetAvailableDatesInput,
  options: { localToday: string; date: string | null; enabled?: boolean }
) {
  const { date } = options
  return useQuery({
    queryKey: [AVAILABILITY_FOR_DATE_QUERY_KEY, ...availabilityContextKey(input, options.localToday), date ?? ""],
    queryFn: ({ signal }) => trpc.telemedicine.getAvailabilityForDate.query({ ...input, date: date ?? "" }, { signal }),
    enabled: date !== null && (options.enabled ?? true),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("availability", "failed to load appointment times")
  })
}

// ---- Booking-flow booking (MEM-719) ----

// Types derived from the client so they can't drift from the server contract.
type BookAppointmentInput = Exclude<Parameters<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>[0], void>
// The booked appointment the confirmation screen renders (assigned provider + UTC visit window).
export type BookedAppointment = Awaited<ReturnType<typeof trpc["telemedicine"]["bookAppointment"]["mutate"]>>

// Redeems the chosen slot and creates the appointment. Not retried: booking is a write, and a
// failure keeps the member on the time picker to retry deliberately.
export function useBookAppointment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BookAppointmentInput) => trpc.telemedicine.bookAppointment.mutate(input),
    // ⚠ NOT awaited: `SelectPreferencesPage` gates the confirmation screen on this mutation, and
    // returning the promise would hold it behind a full re-page of the member's history. Marking
    // the entry stale is synchronous, which is the part that matters.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY })
    },
    meta: bookingBlockedMeta("booking", "failed to book the appointment")
  })
}

// ---- Telemedicine access / dispatch (MEM-677 + MEM-761) ----

// The composite access decision (from the BFF contract): benefit eligibility (`eligible` +
// `programs`) fused with per-program qualification, plus a derived `entry` union the entry points
// dispatch on. Supersedes the old eligibility-only getTelemedicineEntry.
export type TelemedicineAccess = Awaited<ReturnType<typeof trpc["telemedicine"]["getTelemedicineAccess"]["query"]>>

const TELEMEDICINE_ACCESS_QUERY_KEY = "telemedicine.getTelemedicineAccess"

export function useTelemedicineAccess() {
  return useQuery({
    queryKey: [TELEMEDICINE_ACCESS_QUERY_KEY],
    queryFn: () => trpc.telemedicine.getTelemedicineAccess.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    meta: bookingBlockedMeta("access", "failed to load telemedicine access")
  })
}
